import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Calendar, Clock, User, Dog, Scissors, CheckCircle, XCircle, AlertCircle, Filter, DollarSign, Tag, CreditCard, Link as LinkIcon, Archive, RefreshCw, RotateCcw, Layers, FileText, Activity, Camera } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { appointments as appointmentsApi, admin as adminApi, groomers as groomersApi } from '../utils/api';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { formatTo12Hour, parseLocalDate } from '../utils/timeFormat';
import FinancialAdjustmentModal from '../components/FinancialAdjustmentModal';
import PaymentProcessingModal from '../components/PaymentProcessingModal';
import SquarePaymentLinkModal from '../components/SquarePaymentLinkModal';
import PartialPaymentModal from '../components/PartialPaymentModal';
import InstallmentManagementModal from '../components/InstallmentManagementModal';
import InvoiceDataModal from '../components/InvoiceDataModal';
import AppointmentNotesEditor from '../components/AppointmentNotesEditor';
import AppointmentAttachmentsModal from '../components/AppointmentAttachmentsModal';
import usePaymentSettings from '../hooks/usePaymentSettings';
import usePartialPaymentPermission from '../hooks/usePartialPaymentPermission';

/**
 * Fetch the client's own invoice PDF and open it in a new tab. The endpoint
 * is auth-gated, so we cannot use a plain <a href>. Instead: fetch with the
 * Bearer token, wrap the response in a Blob URL, and open that.
 */
const downloadClientInvoice = async (appointmentId) => {
  try {
    const apiBase = process.env.REACT_APP_API_URL || '/api';
    const res = await fetch(`${apiBase}/me/appointments/${appointmentId}/invoice.pdf`, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    toast.error('Could not load invoice: ' + (e.message || 'unknown error'));
  }
};

// An appointment 'needs review' when its scheduled date is in the past but it
// has no final outcome status yet (still pending or confirmed). Admins should
// resolve these to completed / no-show / cancelled / not_completed.
const isNeedsReview = (apt) => {
  if (!apt || !apt.appointment_date) return false;
  if (apt.status !== 'pending' && apt.status !== 'confirmed') return false;
  const aptDate = parseLocalDate(apt.appointment_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return aptDate < today;
};

const AppointmentsEnhanced = () => {
  const { user, isAdmin, isGroomer } = useAuth();
  const { canProcessPayments } = usePaymentSettings();
  const { canProcessPartialPayments } = usePartialPaymentPermission();
  const [allAppointments, setAllAppointments] = useState([]); // Store all appointments for counts
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, confirmed, completed, cancelled
  const [selectedGroomer, setSelectedGroomer] = useState('all');
  const [groomers, setGroomers] = useState([]);
  const [financialModal, setFinancialModal] = useState({ isOpen: false, appointment: null, type: null });
  const [paymentModal, setPaymentModal] = useState({ isOpen: false, appointment: null });
  const [squareLinkModal, setSquareLinkModal] = useState({ isOpen: false, appointment: null });
  const [partialPaymentModal, setPartialPaymentModal] = useState({ isOpen: false, appointment: null });
  const [installmentModal, setInstallmentModal] = useState({ isOpen: false, appointment: null });
  const [invoiceModal, setInvoiceModal] = useState({ isOpen: false, appointment: null });
  const [notesModal, setNotesModal] = useState({ isOpen: false, appointmentId: null });
  const [attachmentsModal, setAttachmentsModal] = useState({ isOpen: false, appointmentId: null });
  const [archiving, setArchiving] = useState(false);
  const [archiveStatus, setArchiveStatus] = useState(null);
  const [allGroomers, setAllGroomers] = useState([]);
  const [reassignDropdown, setReassignDropdown] = useState(null); // appointment id currently showing dropdown
  const [canReassignGroomer, setCanReassignGroomer] = useState(false);
  const [canApproveAppointments, setCanApproveAppointments] = useState(false);

  useEffect(() => {
    loadAppointments();
    if (isAdmin) {
      loadGroomers();
      loadArchiveStatus();
    }
    loadAllGroomers();
    loadReassignSetting();
    loadApprovalSetting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, selectedGroomer]);

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  const loadAllGroomers = async () => {
    try {
      const res = await groomersApi.getAll();
      setAllGroomers(res.data);
    } catch (error) {
      console.error('Load all groomers error:', error);
    }
  };

  const loadApprovalSetting = async () => {
    if (isAdmin) { setCanApproveAppointments(true); return; }
    if (isGroomer) {
      try {
        const token = sessionStorage.getItem('token');
        const res = await axios.get(`${API_URL}/business-info/allow_employee_approval`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { info_value: 'false' } }));
        setCanApproveAppointments(res.data.info_value === 'true');
      } catch {
        setCanApproveAppointments(false);
      }
    }
  };

  const loadReassignSetting = async () => {
    if (isAdmin) {
      setCanReassignGroomer(true);
      return;
    }
    if (isGroomer) {
      try {
        const token = sessionStorage.getItem('token');
        const res = await axios.get(`${API_URL}/business-info/allow_employee_groomer_reassign`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { info_value: 'false' } }));
        setCanReassignGroomer(res.data.info_value === 'true');
      } catch {
        setCanReassignGroomer(false);
      }
    }
  };

  const handleReassignGroomer = async (appointmentId, groomerId) => {
    try {
      await appointmentsApi.reassignGroomer(appointmentId, groomerId || null);
      toast.success('Groomer reassigned successfully');
      setReassignDropdown(null);
      loadAppointments();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reassign employee');
    }
  };

  const loadAppointments = async () => {
    try {
      let response;
      
      if (isAdmin) {
        // Admin sees ALL appointments - always load all for accurate counts
        const params = {};
        if (selectedGroomer !== 'all') params.groomerId = selectedGroomer;
        
        response = await adminApi.getAppointments(params);
      } else {
        // Clients and groomers see their own appointments
        response = await appointmentsApi.getAll();
      }
      
      // Store all appointments for counting
      setAllAppointments(response.data);
      
      // Filter for display
      let filtered;
      if (filter === 'all') {
        filtered = response.data;
      } else if (filter === 'needs_review') {
        filtered = response.data.filter(isNeedsReview);
      } else {
        filtered = response.data.filter(apt => apt.status === filter);
      }

      setAppointments(filtered);
    } catch (error) {
      console.error('Load appointments error:', error);
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const loadArchiveStatus = async () => {
    try {
      const res = await adminApi.getArchiveStatus();
      setArchiveStatus(res.data);
    } catch (error) {
      // Archive status not critical
    }
  };

  const handleArchive = async () => {
    if (!window.confirm('This will email you a summary report of all appointments older than 30 days, then remove them from the system. Appointments from the last 30 days will NOT be affected. Continue?')) {
      return;
    }
    setArchiving(true);
    try {
      const result = await adminApi.archiveAppointments();
      if (result.data.archived === 0) {
        toast.info('No appointments older than 30 days to archive');
      } else {
        toast.success(`Archived ${result.data.archived} appointments. ${result.data.emailSent ? 'Summary report emailed.' : 'Check server logs for the report.'}`);
        loadAppointments();
      }
      loadArchiveStatus();
    } catch (error) {
      console.error('Archive error:', error);
      toast.error('Failed to archive appointments');
    } finally {
      setArchiving(false);
    }
  };

  const loadGroomers = async () => {
    try {
      const response = await adminApi.getAppointments();
      // Extract unique groomers from appointments
      const uniqueGroomers = [...new Map(
        response.data
          .filter(apt => apt.groomer_name)
          .map(apt => [apt.groomer_id, { id: apt.groomer_id, name: apt.groomer_name }])
      ).values()];
      setGroomers(uniqueGroomers);
    } catch (error) {
      console.error('Load groomers error:', error);
    }
  };

  const handleApprove = async (id) => {
    try {
      await adminApi.updateAppointmentStatus(id, { status: 'confirmed' });
      toast.success('Appointment approved!');
      loadAppointments();
    } catch (error) {
      console.error('Approve error:', error);
      toast.error('Failed to approve appointment');
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Are you sure you want to reject this appointment?')) {
      return;
    }

    try {
      await adminApi.updateAppointmentStatus(id, { status: 'cancelled' });
      toast.success('Appointment rejected');
      loadAppointments();
    } catch (error) {
      console.error('Reject error:', error);
      toast.error('Failed to reject appointment');
    }
  };

  const handleClientCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) {
      return;
    }

    try {
      await appointmentsApi.cancel(id);
      toast.success('Appointment cancelled successfully');
      loadAppointments();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error('Failed to cancel appointment');
    }
  };

  const handleComplete = async (id) => {
    try {
      await adminApi.updateAppointmentStatus(id, { status: 'completed' });
      toast.success('Appointment marked as completed');
      loadAppointments();
    } catch (error) {
      console.error('Complete error:', error);
      toast.error('Failed to complete appointment');
    }
  };

  const handleFinancialAdjustment = async (adjustmentData) => {
    const { amount, reason, type, remove } = adjustmentData;
    const appointmentId = financialModal.appointment.id;

    try {
      if (remove) {
        if (type === 'discount') {
          await appointmentsApi.removeDiscount(appointmentId);
          toast.success('Discount removed');
        } else {
          await appointmentsApi.removeRefund(appointmentId);
          toast.success('Refund removed');
        }
      } else {
        if (type === 'discount') {
          await appointmentsApi.applyDiscount(appointmentId, {
            discount_amount: amount,
            discount_reason: reason
          });
          toast.success('Discount applied successfully');
        } else {
          await appointmentsApi.applyRefund(appointmentId, {
            refund_amount: amount,
            refund_reason: reason
          });
          toast.success('Refund applied successfully');
        }
      }
      loadAppointments();
    } catch (error) {
      console.error('Financial adjustment error:', error);
      throw error;
    }
  };

  const openFinancialModal = (appointment, type) => {
    setFinancialModal({ isOpen: true, appointment, type });
  };

  const closeFinancialModal = () => {
    setFinancialModal({ isOpen: false, appointment: null, type: null });
  };

  const openPaymentModal = (appointment) => {
    setPaymentModal({ isOpen: true, appointment });
  };

  const closePaymentModal = () => {
    setPaymentModal({ isOpen: false, appointment: null });
  };

  const handleProcessPayment = async (paymentData) => {
    const appointmentId = paymentModal.appointment.id;
    
    try {
      await appointmentsApi.processPayment(appointmentId, paymentData);
      toast.success('Payment processed successfully');
      loadAppointments();
    } catch (error) {
      console.error('Payment processing error:', error);
      throw error;
    }
  };

  const handlePartialPayment = async (paymentData) => {
    const appointmentId = partialPaymentModal.appointment.id;
    
    try {
      const token = sessionStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || '/api';
      
      await fetch(`${API_URL}/payments/partial-payment/${appointmentId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(paymentData)
      }).then(res => res.json());
      
      loadAppointments();
    } catch (error) {
      console.error('Partial payment error:', error);
      throw error;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', icon: AlertCircle },
      confirmed: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', icon: CheckCircle },
      completed: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', icon: CheckCircle },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', icon: XCircle },
      no_show: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300', icon: XCircle },
      not_completed: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', icon: AlertCircle },
    };

    const style = styles[status] || styles.pending;
    const Icon = style.icon;

    const displayText = status === 'no_show' ? 'No Show' : 
                       status === 'not_completed' ? 'Not Completed' :
                       status === 'pending' ? 'Pending Approval' :
                       status.charAt(0).toUpperCase() + status.slice(1);

    return (
      <div className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold border ${style.bg} ${style.text} ${style.border}`}>
        <Icon size={14} />
        <span>{displayText}</span>
      </div>
    );
  };

  const formatDateTime = (date, time) => {
    const dateObj = parseLocalDate(date);
    return {
      date: format(dateObj, 'EEEE, MMMM d, yyyy'),
      time: time,
    };
  };

  const groupAppointmentsByStatus = () => {
    const grouped = {
      pending: [],
      confirmed: [],
      completed: [],
      cancelled: [],
      no_show: [],
      not_completed: [],
    };

    // Use allAppointments for accurate counts
    allAppointments.forEach(apt => {
      if (grouped[apt.status]) {
        grouped[apt.status].push(apt);
      }
    });

    return grouped;
  };

  const renderAppointmentCard = (appointment) => {
    const { date } = formatDateTime(appointment.appointment_date, appointment.start_time);
    const isPending = appointment.status === 'pending';
    const isConfirmed = appointment.status === 'confirmed';
    const isNotCompleted = appointment.status === 'not_completed';
    const isNoShow = appointment.status === 'no_show';
    const needsReview = isNeedsReview(appointment);

    return (
      <div
        key={appointment.id}
        className={`card hover:shadow-lg transition-shadow ${needsReview ? 'ring-2 ring-red-500 bg-red-50' : ''}`}
      >
        {needsReview && (
          <div className="flex items-center space-x-2 mb-3 p-2 bg-red-100 border border-red-300 rounded-lg text-sm">
            <AlertCircle size={16} className="text-red-600 flex-shrink-0" />
            <span className="text-red-800 font-semibold">
              Needs Review &mdash; appointment date has passed without a final status
            </span>
          </div>
        )}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {/* Client Info (for admin/groomer view) */}
            {(isAdmin || isGroomer) && (
              <div className="flex items-center space-x-2 mb-3">
                <User size={18} className="text-primary-600" />
                <div>
                  <span className="font-semibold text-gray-900">
                    {appointment.client_name}
                  </span>
                  <span className="text-gray-500 text-sm ml-2">
                    ({appointment.client_email})
                  </span>
                </div>
              </div>
            )}

            {/* Pet Info */}
            <div className="flex items-center space-x-2 mb-2">
              <Dog size={16} className="text-gray-500" />
              <span className="text-gray-700">
                {appointment.pet_name} - {appointment.pet_breed} ({appointment.pet_size})
              </span>
            </div>

            {/* Service Info */}
            <div className="flex items-center space-x-2 mb-2">
              <Scissors size={16} className="text-gray-500" />
              <span className="text-gray-700">{appointment.service_name || 'Standard Appointment'}</span>
            </div>

            {/* Payment Status */}
            {appointment.payment_status && appointment.payment_status !== 'pending' && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <CreditCard size={16} className="text-blue-600" />
                    <span className="text-sm font-semibold text-blue-900">
                      Payment: {appointment.payment_status === 'paid' ? 'Paid in Full' : 
                               appointment.payment_status === 'partial' ? 'Partial Payment' : 
                               appointment.payment_status === 'refunded' ? 'Refunded' : 
                               appointment.payment_status}
                    </span>
                  </div>
                  {appointment.amount_paid > 0 && (
                    <span className="text-sm font-bold text-blue-600">
                      ${parseFloat(appointment.amount_paid).toFixed(2)}
                    </span>
                  )}
                </div>
                {appointment.payment_method && (
                  <div className="text-xs text-blue-700 mt-1">
                    Method: {appointment.payment_method.toUpperCase()}
                    {appointment.payment_transaction_id && ` • ID: ${appointment.payment_transaction_id}`}
                  </div>
                )}
              </div>
            )}

            {/* Pricing Info */}
            <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Original Price:</span>
                <span className="font-semibold">${parseFloat(appointment.total_price || 0).toFixed(2)}</span>
              </div>
              {appointment.addons && appointment.addons.length > 0 && (
                <div className="pl-2 border-l-2 border-primary-200 ml-1 space-y-0.5">
                  {appointment.addons.map((addon, idx) => (
                    <div key={idx} className="flex justify-between text-gray-500 text-xs">
                      <span>+ {addon.addon_name}{addon.quantity > 1 ? ` x${addon.quantity}` : ''}</span>
                      <span>${parseFloat(addon.price * (addon.quantity || 1)).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
              {appointment.discount_amount > 0 && (
                <div className="flex justify-between text-orange-600">
                  <span className="flex items-center space-x-1">
                    <Tag size={14} />
                    <span>Discount:</span>
                  </span>
                  <span className="font-semibold">-${parseFloat(appointment.discount_amount).toFixed(2)}</span>
                </div>
              )}
              {appointment.refund_amount > 0 && (
                <div className="flex justify-between text-red-600">
                  <span className="flex items-center space-x-1">
                    <DollarSign size={14} />
                    <span>Refund:</span>
                  </span>
                  <span className="font-semibold">-${parseFloat(appointment.refund_amount).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="font-semibold text-gray-900">Final Price:</span>
                <span className="font-bold text-primary-600">
                  ${parseFloat(appointment.final_price || appointment.total_price || 0).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Discount/Refund Reasons */}
            {(appointment.discount_reason || appointment.refund_reason) && (
              <div className="mt-2 space-y-1">
                {appointment.discount_reason && (
                  <div className="text-xs text-orange-700 bg-orange-50 p-2 rounded">
                    <strong>Discount Reason:</strong> {appointment.discount_reason}
                  </div>
                )}
                {appointment.refund_reason && (
                  <div className="text-xs text-red-700 bg-red-50 p-2 rounded">
                    <strong>Refund Reason:</strong> {appointment.refund_reason}
                  </div>
                )}
              </div>
            )}

            {/* Date & Time */}
            <div className="flex items-center space-x-2 mb-2">
              <Calendar size={16} className="text-gray-500" />
              <span className="text-gray-700">{date}</span>
            </div>

            <div className="flex items-center space-x-2 mb-2">
              <Clock size={16} className="text-gray-500" />
              <span className="text-gray-700">
                {formatTo12Hour(appointment.start_time)} - {formatTo12Hour(appointment.end_time)}
              </span>
            </div>

            {/* Groomer Info with Reassign */}
            <div className="flex items-center space-x-2 mb-2">
              <User size={16} className="text-gray-500" />
              <span className="text-gray-600 text-sm">
                Groomer: {appointment.groomer_name || appointment.groomer_first_name 
                  ? (appointment.groomer_name || `${appointment.groomer_first_name} ${appointment.groomer_last_name}`)
                  : <span className="text-orange-500 italic">Unassigned</span>}
              </span>
              {canReassignGroomer && appointment.status !== 'completed' && appointment.status !== 'cancelled' && (
                <button
                  onClick={() => setReassignDropdown(reassignDropdown === appointment.id ? null : appointment.id)}
                  className="ml-2 text-primary-600 hover:text-primary-800 transition-colors"
                  title="Reassign Groomer"
                >
                  <RefreshCw size={14} />
                </button>
              )}
            </div>
            {reassignDropdown === appointment.id && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <label className="block text-xs font-semibold text-blue-800 mb-1">Reassign Groomer:</label>
                <select
                  defaultValue={appointment.groomer_id || ''}
                  onChange={(e) => handleReassignGroomer(appointment.id, e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-blue-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Unassign</option>
                  {allGroomers.map(g => (
                    <option key={g.id} value={g.id}>
                      {g.first_name} {g.last_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Special Requests */}
            {appointment.special_requests && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 italic">
                  <strong>Special Requests:</strong> {appointment.special_requests}
                </p>
              </div>
            )}
          </div>

          {/* Status and Actions */}
          <div className="flex flex-col items-end space-y-2 ml-4">
            {getStatusBadge(appointment.status)}

            {/* Admin Actions */}
            {isAdmin && (
              <div className="flex flex-col space-y-2 mt-2">
                {isPending && (
                  <>
                    <button
                      onClick={() => handleApprove(appointment.id)}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      <CheckCircle size={16} />
                      <span>Approve</span>
                    </button>
                    <button
                      onClick={() => handleReject(appointment.id)}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                    >
                      <XCircle size={16} />
                      <span>Reject</span>
                    </button>
                  </>
                )}
                {isConfirmed && (
                  <>
                    <button
                      onClick={() => handleComplete(appointment.id)}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      <CheckCircle size={16} />
                      <span>Mark Complete</span>
                    </button>
                    <button
                      onClick={() => adminApi.updateAppointmentStatus(appointment.id, { status: 'no_show' }).then(() => { toast.success('Marked as No Show'); loadAppointments(); }).catch(() => toast.error('Failed to update'))}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                    >
                      <XCircle size={16} />
                      <span>Mark No Show</span>
                    </button>
                    <button
                      onClick={() => adminApi.updateAppointmentStatus(appointment.id, { status: 'not_completed' }).then(() => { toast.success('Marked as Not Completed'); loadAppointments(); }).catch(() => toast.error('Failed to update'))}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm"
                    >
                      <AlertCircle size={16} />
                      <span>Mark Not Completed</span>
                    </button>
                    <button
                      onClick={() => handleReject(appointment.id)}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                    >
                      <XCircle size={16} />
                      <span>Cancel</span>
                    </button>
                  </>
                )}
                {isNotCompleted && (
                  <>
                    <button
                      onClick={() => handleComplete(appointment.id)}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      <CheckCircle size={16} />
                      <span>Mark Complete</span>
                    </button>
                    <button
                      onClick={() => adminApi.updateAppointmentStatus(appointment.id, { status: 'no_show' }).then(() => { toast.success('Marked as No Show'); loadAppointments(); }).catch(() => toast.error('Failed to update'))}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                    >
                      <XCircle size={16} />
                      <span>Mark No Show</span>
                    </button>
                    <button
                      onClick={() => { if (window.confirm('Cancel this appointment?')) { adminApi.updateAppointmentStatus(appointment.id, { status: 'cancelled' }).then(() => { toast.success('Appointment cancelled'); loadAppointments(); }).catch(() => toast.error('Failed to cancel')); }}}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                    >
                      <XCircle size={16} />
                      <span>Cancel</span>
                    </button>
                  </>
                )}
                {isNoShow && (
                  <>
                    <button
                      onClick={() => handleComplete(appointment.id)}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      <CheckCircle size={16} />
                      <span>Mark Complete</span>
                    </button>
                    <button
                      onClick={() => { if (window.confirm('Cancel this appointment?')) { adminApi.updateAppointmentStatus(appointment.id, { status: 'cancelled' }).then(() => { toast.success('Appointment cancelled'); loadAppointments(); }).catch(() => toast.error('Failed to cancel')); }}}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                    >
                      <XCircle size={16} />
                      <span>Cancel</span>
                    </button>
                  </>
                )}
                {/* Payment Buttons */}
                {canProcessPayments && (appointment.payment_status === 'pending' || appointment.payment_status === 'partial') && (
                  <div className="pt-2 border-t border-gray-200 space-y-2">
                    {canProcessPartialPayments && (
                      <button
                        onClick={() => setPartialPaymentModal({ isOpen: true, appointment })}
                        className="flex items-center space-x-1 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm w-full justify-center"
                      >
                        <DollarSign size={16} />
                        <span>Record Partial Payment</span>
                      </button>
                    )}
                    <button
                      onClick={() => openPaymentModal(appointment)}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm w-full justify-center"
                    >
                      <CreditCard size={16} />
                      <span>Process Online Payment</span>
                    </button>
                    <button
                      onClick={() => setSquareLinkModal({ isOpen: true, appointment })}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm w-full justify-center"
                    >
                      <LinkIcon size={16} />
                      <span>Link Square Payment</span>
                    </button>
                    {/* Installment plan manager. Surfaced for any partial-paid
                        appointment — the modal itself shows a graceful empty
                        state if the appointment doesn't actually have a plan. */}
                    {appointment.payment_status === 'partial' && (
                      <button
                        onClick={() => setInstallmentModal({ isOpen: true, appointment })}
                        className="flex items-center space-x-1 px-3 py-1.5 bg-sky-100 text-sky-700 rounded-lg hover:bg-sky-200 transition-colors text-sm w-full justify-center"
                      >
                        <Layers size={16} />
                        <span>Manage Installments</span>
                      </button>
                    )}
                  </div>
                )}
                {/* Financial Adjustment Buttons */}
                <div className="pt-2 border-t border-gray-200">
                  <button
                    onClick={() => openFinancialModal(appointment, 'discount')}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors text-sm w-full justify-center mb-2"
                  >
                    <Tag size={16} />
                    <span>{appointment.discount_amount > 0 ? 'Edit Discount' : 'Add Discount'}</span>
                  </button>
                  <button
                    onClick={() => openFinancialModal(appointment, 'refund')}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm w-full justify-center"
                  >
                    <DollarSign size={16} />
                    <span>{appointment.refund_amount > 0 ? 'Edit Refund' : 'Add Refund'}</span>
                  </button>
                  <button
                    onClick={() => setInvoiceModal({ isOpen: true, appointment })}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors text-sm w-full justify-center mt-2"
                  >
                    <FileText size={16} />
                    <span>Invoice / Receipt</span>
                  </button>
                  <button
                    onClick={() => setNotesModal({ isOpen: true, appointmentId: appointment.id })}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors text-sm w-full justify-center mt-2"
                    title="Add or edit notes for this appointment"
                  >
                    <FileText size={16} />
                    <span>Notes</span>
                  </button>
                  {appointment.pet_id && (
                    <RouterLink
                      to={`/pets/${appointment.pet_id}/chart`}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm w-full justify-center mt-2"
                      title="Open this subject's full chart"
                    >
                      <Activity size={16} />
                      <span>Chart</span>
                    </RouterLink>
                  )}
                  <button
                    onClick={() => setAttachmentsModal({ isOpen: true, appointmentId: appointment.id })}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-pink-100 text-pink-700 rounded-lg hover:bg-pink-200 transition-colors text-sm w-full justify-center mt-2"
                    title="Upload before/after photos or other files"
                  >
                    <Camera size={16} />
                    <span>Photos</span>
                  </button>
                </div>
              </div>
            )}

            {/* Groomer Actions */}
            {isGroomer && !isAdmin && isPending && canApproveAppointments && (
              <div className="flex flex-col space-y-2 mt-2">
                <button
                  onClick={() => handleApprove(appointment.id)}
                  className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                >
                  <CheckCircle size={16} />
                  <span>Approve</span>
                </button>
              </div>
            )}
            {isGroomer && !isAdmin && isConfirmed && (
              <div className="flex flex-col space-y-2 mt-2">
                <button
                  onClick={() => handleComplete(appointment.id)}
                  className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <CheckCircle size={16} />
                  <span>Mark Complete</span>
                </button>
              </div>
            )}

            {/* Groomers can record notes against any appointment assigned
                to them, regardless of pending/confirmed/completed status,
                and can open the subject's full chart from the same row. */}
            {isGroomer && !isAdmin && (
              <div className="flex flex-col space-y-2 mt-2">
                <button
                  onClick={() => setNotesModal({ isOpen: true, appointmentId: appointment.id })}
                  className="flex items-center space-x-1 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors text-sm"
                  title="Add or edit notes for this appointment"
                >
                  <FileText size={16} />
                  <span>Notes</span>
                </button>
                {appointment.pet_id && (
                  <RouterLink
                    to={`/pets/${appointment.pet_id}/chart`}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
                    title="Open this subject's full chart"
                  >
                    <Activity size={16} />
                    <span>Chart</span>
                  </RouterLink>
                )}
                <button
                  onClick={() => setAttachmentsModal({ isOpen: true, appointmentId: appointment.id })}
                  className="flex items-center space-x-1 px-3 py-1.5 bg-pink-100 text-pink-700 rounded-lg hover:bg-pink-200 transition-colors text-sm"
                  title="Upload before/after photos or other files"
                >
                  <Camera size={16} />
                  <span>Photos</span>
                </button>
              </div>
            )}

            {/* Client Actions */}
            {!isAdmin && !isGroomer && (
              <div className="flex flex-col space-y-2 mt-2">
                {(isPending || isConfirmed) && (
                  <button
                    onClick={() => handleClientCancel(appointment.id)}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                  >
                    <XCircle size={16} />
                    <span>Cancel Appointment</span>
                  </button>
                )}
                {/* Download invoice — available to clients for any of their
                    appointments. The backend endpoint will gracefully return
                    the appropriate template if no per-appointment override
                    exists. */}
                <button
                  onClick={() => downloadClientInvoice(appointment.id)}
                  className="flex items-center space-x-1 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors text-sm"
                  title="Download a PDF invoice / receipt for this appointment"
                >
                  <FileText size={16} />
                  <span>Download invoice (PDF)</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const grouped = groupAppointmentsByStatus();
  const needsReviewAppointments = allAppointments.filter(isNeedsReview);
  const needsReviewCount = needsReviewAppointments.length;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <PostBookingFormsBanner />
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Calendar className="text-primary-600" size={32} />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isAdmin ? 'All Appointments' : isGroomer ? 'My Schedule' : 'My Appointments'}
              </h1>
              <p className="text-gray-600 text-sm mt-1">
                {isAdmin && 'Manage all client appointments'}
                {isGroomer && 'View your assigned appointments'}
                {!isAdmin && !isGroomer && 'View and manage your bookings'}
              </p>
            </div>
          </div>
          {isAdmin ? (
            <div className="flex flex-col items-end space-y-1">
              <button
                onClick={handleArchive}
                disabled={archiving}
                className="btn btn-secondary flex items-center space-x-2 text-sm"
              >
                <Archive size={16} />
                <span>{archiving ? 'Archiving...' : 'Archive Old Appointments'}</span>
              </button>
              {archiveStatus?.last_archive_date && (
                <p className="text-xs text-gray-500">
                  Last archived: {new Date(archiveStatus.last_archive_date).toLocaleDateString()}
                  {archiveStatus.last_archive_count && ` (${archiveStatus.last_archive_count} removed)`}
                </p>
              )}
            </div>
          ) : (
            <button
              onClick={loadAppointments}
              className="btn btn-secondary flex items-center space-x-2 text-sm"
              title="Refresh appointments"
            >
              <RotateCcw size={16} />
              <span>Refresh</span>
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="card mb-6">
          <div className="flex items-center space-x-4">
            <Filter size={20} className="text-gray-600" />
            <div className="flex space-x-2 flex-1 flex-wrap gap-2">
              {['all', 'needs_review', 'pending', 'confirmed', 'completed', 'not_completed', 'cancelled', 'no_show'].map((status) => {
                const isReviewFilter = status === 'needs_review';
                const count = isReviewFilter
                  ? needsReviewCount
                  : (grouped[status]?.length || 0);
                const label = status === 'no_show' ? 'No Show'
                  : status === 'not_completed' ? 'Not Completed'
                  : isReviewFilter ? 'Needs Review'
                  : status.charAt(0).toUpperCase() + status.slice(1);
                const isActive = filter === status;
                const reviewActiveStyle = isReviewFilter && !isActive && count > 0
                  ? 'bg-red-100 text-red-800 hover:bg-red-200 border border-red-300'
                  : '';
                return (
                  <button
                    key={status}
                    onClick={() => setFilter(status)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      isActive
                        ? (isReviewFilter ? 'bg-red-600 text-white' : 'bg-primary-600 text-white')
                        : reviewActiveStyle || 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {label}
                    {status !== 'all' && (
                      <span className="ml-2 text-xs">({count})</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Groomer Filter (Admin only) */}
            {isAdmin && groomers.length > 0 && (
              <select
                value={selectedGroomer}
                onChange={(e) => setSelectedGroomer(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Groomers</option>
                {groomers.map(groomer => (
                  <option key={groomer.id} value={groomer.id}>
                    {groomer.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Appointments List */}
        {filter === 'all' ? (
          // Grouped view for "All"
          <div className="space-y-8">
            {/* Needs Review (past-date appointments still pending or confirmed) */}
            {needsReviewCount > 0 && (
              <div>
                <h2 className="text-xl font-bold text-red-700 mb-4 flex items-center space-x-2">
                  <AlertCircle className="text-red-600" size={24} />
                  <span>Needs Review ({needsReviewCount})</span>
                </h2>
                <p className="text-sm text-gray-600 mb-3">
                  These appointments are past their scheduled date but still pending or confirmed. Mark each as completed, no-show, or cancelled.
                </p>
                <div className="space-y-4">
                  {needsReviewAppointments.map(renderAppointmentCard)}
                </div>
              </div>
            )}

            {/* Pending Approval */}
            {grouped.pending.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <AlertCircle className="text-yellow-600" size={24} />
                  <span>Pending Approval ({grouped.pending.length})</span>
                </h2>
                <div className="space-y-4">
                  {grouped.pending.map(renderAppointmentCard)}
                </div>
              </div>
            )}

            {/* Confirmed/Upcoming */}
            {grouped.confirmed.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <CheckCircle className="text-green-600" size={24} />
                  <span>Confirmed ({grouped.confirmed.length})</span>
                </h2>
                <div className="space-y-4">
                  {grouped.confirmed.map(renderAppointmentCard)}
                </div>
              </div>
            )}

            {/* Completed */}
            {grouped.completed.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <CheckCircle className="text-blue-600" size={24} />
                  <span>Completed ({grouped.completed.length})</span>
                </h2>
                <div className="space-y-4">
                  {grouped.completed.map(renderAppointmentCard)}
                </div>
              </div>
            )}

            {/* Cancelled */}
            {grouped.cancelled.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <XCircle className="text-red-600" size={24} />
                  <span>Cancelled ({grouped.cancelled.length})</span>
                </h2>
                <div className="space-y-4">
                  {grouped.cancelled.map(renderAppointmentCard)}
                </div>
              </div>
            )}

            {/* Not Completed */}
            {grouped.not_completed.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <AlertCircle className="text-orange-600" size={24} />
                  <span>Not Completed ({grouped.not_completed.length})</span>
                </h2>
                <div className="space-y-4">
                  {grouped.not_completed.map(renderAppointmentCard)}
                </div>
              </div>
            )}

            {/* No Show */}
            {grouped.no_show.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <XCircle className="text-gray-600" size={24} />
                  <span>No Show ({grouped.no_show.length})</span>
                </h2>
                <div className="space-y-4">
                  {grouped.no_show.map(renderAppointmentCard)}
                </div>
              </div>
            )}

            {appointments.length === 0 && (
              <div className="card text-center py-12">
                <Calendar className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-600 text-lg">No appointments found</p>
              </div>
            )}
          </div>
        ) : (
          // Filtered view
          <div className="space-y-4">
            {appointments.length === 0 ? (
              <div className="card text-center py-12">
                <Calendar className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-600 text-lg">
                  No {filter === 'no_show' ? 'no show' : filter === 'not_completed' ? 'not completed' : filter === 'needs_review' ? 'appointments needing review' : `${filter} appointments`} found
                </p>
              </div>
            ) : (
              appointments.map(renderAppointmentCard)
            )}
          </div>
        )}

        {/* Financial Adjustment Modal */}
        <FinancialAdjustmentModal
          isOpen={financialModal.isOpen}
          onClose={closeFinancialModal}
          appointment={financialModal.appointment}
          onSave={handleFinancialAdjustment}
          type={financialModal.type}
        />

        {/* Payment Processing Modal */}
        <PaymentProcessingModal
          isOpen={paymentModal.isOpen}
          onClose={closePaymentModal}
          appointment={paymentModal.appointment}
          onSave={handleProcessPayment}
        />

        {/* Square Payment Link Modal */}
        <SquarePaymentLinkModal
          isOpen={squareLinkModal.isOpen}
          onClose={() => setSquareLinkModal({ isOpen: false, appointment: null })}
          appointment={squareLinkModal.appointment}
          onSuccess={loadAppointments}
        />

        {/* Partial Payment Modal */}
        <PartialPaymentModal
          isOpen={partialPaymentModal.isOpen}
          onClose={() => setPartialPaymentModal({ isOpen: false, appointment: null })}
          appointment={partialPaymentModal.appointment}
          onSuccess={handlePartialPayment}
        />

        {/* Installment Plan Modal (Phase 2b) */}
        <InstallmentManagementModal
          isOpen={installmentModal.isOpen}
          onClose={() => setInstallmentModal({ isOpen: false, appointment: null })}
          appointment={installmentModal.appointment}
          onUpdate={loadAppointments}
        />

        {/* Invoice / Receipt Modal */}
        <InvoiceDataModal
          isOpen={invoiceModal.isOpen}
          onClose={() => setInvoiceModal({ isOpen: false, appointment: null })}
          appointment={invoiceModal.appointment}
          onSaved={loadAppointments}
        />

        {/* Appointment Notes Modal (free-form / SOAP / custom format) */}
        {notesModal.isOpen && (
          <AppointmentNotesEditor
            appointmentId={notesModal.appointmentId}
            userRole={isAdmin ? 'admin' : 'groomer'}
            onClose={() => setNotesModal({ isOpen: false, appointmentId: null })}
            onSaved={loadAppointments}
          />
        )}

        {/* Appointment Attachments Modal (item #11 Phase 2 — photos / PDFs) */}
        {attachmentsModal.isOpen && (
          <AppointmentAttachmentsModal
            appointmentId={attachmentsModal.appointmentId}
            currentUserId={user?.id}
            userRole={isAdmin ? 'admin' : 'groomer'}
            onClose={() => setAttachmentsModal({ isOpen: false, appointmentId: null })}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Banner rendered when the just-completed booking spawned one or more
 * attached intake forms (Phase 2b of item #9). The booking page writes
 * `pendingFormsPostBooking` into sessionStorage; we read it once on mount
 * and keep it visible until the user dismisses it explicitly, so a
 * PayPal/Square redirect doesn't lose the links. Each link opens the
 * public /forms/fill/:token page in a new tab.
 *
 * Deliberately not tied to a specific appointment row — the banner is a
 * post-booking nudge, not per-card state. Per-card "intake needed" badges
 * would require a backend lookup endpoint and are a future enhancement.
 */
function PostBookingFormsBanner() {
  const [pending, setPending] = useState(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('pendingFormsPostBooking');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) setPending(parsed);
    } catch (_) { /* ignore */ }
  }, []);

  const dismiss = () => {
    try { sessionStorage.removeItem('pendingFormsPostBooking'); } catch (_) {}
    setPending(null);
  };

  if (!pending || pending.length === 0) return null;

  return (
    <div className="mb-6 bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
      <div className="flex items-start justify-between space-x-3">
        <div className="flex items-start space-x-3">
          <FileText className="text-blue-700 mt-0.5 shrink-0" size={20} />
          <div>
            <h3 className="font-bold text-blue-900">
              Please complete {pending.length} intake form{pending.length === 1 ? '' : 's'} before your appointment
            </h3>
            <p className="text-sm text-blue-800 mt-1">
              Your booked service requires paperwork. Click each link below to fill it in — takes just a couple of minutes.
            </p>
            <ul className="mt-2 space-y-1">
              {pending.map((f) => (
                <li key={f.submission_id}>
                  <a
                    href={`/forms/fill/${f.share_token}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-blue-700 hover:text-blue-900 underline"
                  >
                    {f.form_name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-blue-700 hover:text-blue-900 shrink-0"
          title="Dismiss"
        >
          <XCircle size={20} />
        </button>
      </div>
    </div>
  );
}

export default AppointmentsEnhanced;
